import { useSearchParams } from 'react-router-dom'
import { useSession } from '../context/SessionContext'
import { startDiscordLogin } from '../services/auth'
import { useAsync } from '../hooks/useAsync'
import { fetchHeadToHead, fetchPlayerHistory } from '../services/league'
import {
  Avatar,
  Card,
  ConferenceBadge,
  EmptyState,
  Loading,
  Notice,
  SortableHeaderRow,
  formatRecord,
  formatSigned,
  recordSortKey,
  useTableSort,
  type SortableColumn,
} from '../components/ui'
import type { HeadToHeadRow, SeasonRecordBreakdown } from '../types'

const SEASON_COLUMNS: Array<SortableColumn<SeasonRecordBreakdown>> = [
  { key: 'season', label: 'Season', className: 'num', sort: row => row.seasonNumber },
  { key: 'conference', label: 'Conf', sortLabel: 'conference', sort: row => row.conference },
  {
    key: 'record',
    label: 'Record',
    className: 'record',
    hint: 'Sort by wins, then by fewest losses',
    sort: row => recordSortKey(row.wins, row.losses),
  },
  {
    key: 'diff',
    label: 'Diff',
    className: 'num',
    sortLabel: 'point differential',
    sort: row => row.pointsFor - row.pointsAgainst,
  },
]

function SeasonsTable({ rows }: { rows: SeasonRecordBreakdown[] }) {
  const sort = useTableSort(rows, SEASON_COLUMNS)

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <SortableHeaderRow columns={SEASON_COLUMNS} sort={sort} />
        </thead>
        <tbody>
          {sort.rows.map(season => (
            <tr key={season.seasonNumber}>
              <td className="num">{season.seasonNumber}</td>
              <td>
                <ConferenceBadge conference={season.conference} />
              </td>
              <td className="record">{formatRecord(season.wins, season.losses, season.ties)}</td>
              <td className="num">{formatSigned(season.pointsFor - season.pointsAgainst)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const RIVAL_COLUMNS: Array<SortableColumn<HeadToHeadRow>> = [
  { key: 'opponent', label: 'Opponent', sort: row => row.opponentDisplayName },
  {
    key: 'record',
    label: 'Record',
    className: 'record',
    hint: 'Sort by wins, then by fewest losses',
    sort: row => recordSortKey(row.wins, row.losses),
  },
  {
    key: 'gamesPlayed',
    label: 'GP',
    className: 'num',
    sortLabel: 'games played',
    sort: row => row.gamesPlayed,
  },
]

function RivalsTable({ rows }: { rows: HeadToHeadRow[] }) {
  const sort = useTableSort(rows, RIVAL_COLUMNS)

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <SortableHeaderRow columns={RIVAL_COLUMNS} sort={sort} />
        </thead>
        <tbody>
          {sort.rows.map(row => (
            <tr key={row.opponentUserId}>
              <td>{row.opponentDisplayName}</td>
              <td className="record">{formatRecord(row.wins, row.losses, row.ties)}</td>
              <td className="num">{row.gamesPlayed}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Maps the OAuth callback's `?status=` value to a message. */
const STATUS_MESSAGES: Record<string, { tone: 'success' | 'error'; text: string }> = {
  success: { tone: 'success', text: 'Signed in with Discord.' },
  error: { tone: 'error', text: 'Discord sign-in failed. Please try again.' },
  missing_code: { tone: 'error', text: 'Discord did not return an authorization code.' },
}

export default function Account() {
  const { user, leaguePlayer, isAdmin, loading, signOut } = useSession()
  const [searchParams] = useSearchParams()
  const status = searchParams.get('status')
  const statusMessage = status ? STATUS_MESSAGES[status] : undefined

  const history = useAsync(
    () => (leaguePlayer ? fetchPlayerHistory(leaguePlayer.id) : Promise.resolve([])),
    [leaguePlayer?.id]
  )
  const rivals = useAsync(
    () => (leaguePlayer ? fetchHeadToHead(leaguePlayer.id) : Promise.resolve([])),
    [leaguePlayer?.id]
  )

  if (loading) {
    return (
      <Card title="Account">
        <Loading rows={3} />
      </Card>
    )
  }

  if (!user) {
    return (
      <div className="stack">
        <header className="page-header">
          <div>
            <span className="eyebrow">Account</span>
            <h1>Sign in</h1>
            <p>Sign in with Discord to upload game results</p>
          </div>
        </header>

        {statusMessage && <Notice tone={statusMessage.tone}>{statusMessage.text}</Notice>}

        <Card>
          <EmptyState title="Not signed in">
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => startDiscordLogin()}
              style={{ marginTop: 12 }}
            >
              Sign in with Discord
            </button>
          </EmptyState>
        </Card>
      </div>
    )
  }

  const totals = (history.data ?? []).reduce(
    (accumulator, season) => ({
      wins: accumulator.wins + season.wins,
      losses: accumulator.losses + season.losses,
      ties: accumulator.ties + season.ties,
    }),
    { wins: 0, losses: 0, ties: 0 }
  )

  return (
    <div className="stack">
      <header className="page-header">
        <div>
          <span className="eyebrow">Account</span>
          <h1>{user.displayName}</h1>
        </div>
        <button type="button" className="btn btn--ghost" onClick={() => void signOut()}>
          Sign out
        </button>
      </header>

      {statusMessage && <Notice tone={statusMessage.tone}>{statusMessage.text}</Notice>}

      <Card title="Profile">
        <div className="row" style={{ alignItems: 'flex-start', gap: 20 }}>
          <Avatar
            name={user.displayName}
            discordId={user.discordId}
            avatar={user.discordAvatar}
            size="lg"
          />
          <div className="stack-sm">
            <div>
              <div className="stat__label">Discord</div>
              <div>{user.discordUsername ?? user.username}</div>
            </div>
            <div>
              <div className="stat__label">Role</div>
              <div>{isAdmin ? 'Administrator' : 'League member'}</div>
            </div>
            <div>
              <div className="stat__label">Career record</div>
              <div>{formatRecord(totals.wins, totals.losses, totals.ties)}</div>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid-2">
        <Card title="Your seasons" flush>
          {history.loading ? (
            <div style={{ padding: 24 }}>
              <Loading rows={3} />
            </div>
          ) : history.data && history.data.length > 0 ? (
            <SeasonsTable rows={history.data} />
          ) : (
            <EmptyState title="No games recorded yet">
              Upload a save state to get on the board.
            </EmptyState>
          )}
        </Card>

        <Card title="Your rivalries" flush>
          {rivals.loading ? (
            <div style={{ padding: 24 }}>
              <Loading rows={3} />
            </div>
          ) : rivals.data && rivals.data.length > 0 ? (
            <RivalsTable rows={rivals.data} />
          ) : (
            <EmptyState title="No head-to-head history yet" />
          )}
        </Card>
      </div>
    </div>
  )
}
